/**
 * svgParallelo — outillage SVG partagé du dossier parallélogrammes (5e, thème #11).
 *
 * Un parallélogramme est construit EXACTEMENT à partir de deux vecteurs u et v
 * issus d'un sommet A : sommets A, B = A+u, C = A+u+v, D = A+v (ordre A-B-C-D
 * autour de la figure). Les côtés opposés sont donc réellement parallèles et
 * égaux, le centre O = milieu des diagonales, les angles opposés égaux — la
 * géométrie dessinée respecte les propriétés que l'exercice interroge.
 *
 * Réutilise le moteur `creerFigure` de svgAngles (segment, point, marque
 * d'angle en polyligne, codageDroit, codageLongueur) via svgTriangles + le
 * `cadreur` (échelle uniforme → angles et égalités conservés). Coordonnées
 * d'entrée en repère ÉCRAN (y vers le bas ; on met y négatif pour « vers le
 * haut »), cadrées ensuite dans le viewBox.
 *
 * Markup balisé (solveur géométrique éventuel) :
 *   <text class="mv">55°</text>  mesure d'angle marquée (sur la bissectrice)
 *   <g class="pt">…<text class="pl">B</text></g>  sommet nommé
 */

import {
    creerFigure, cadreur, figEl, milieu, dist, angleVers,
    marqueInterieur, codageDroitInterieur, segmentPointille,
    BLEU, ROUGE, VERT, chapeau, nomAngle,
} from '../triangles/svgTriangles';

export {
    creerFigure, cadreur, figEl, milieu, dist, angleVers,
    marqueInterieur, codageDroitInterieur, segmentPointille,
    BLEU, ROUGE, VERT, chapeau, nomAngle,
};

/** Salt de variabilité (entiers concaténés) pour data-v en tête de SVG. */
export const salt = (...n) => n.map((x) => Math.round(x)).join('_');

/**
 * Sommets d'un parallélogramme depuis A et deux vecteurs u, v (ordre A-B-C-D).
 * u = A→B (et D→C), v = A→D (et B→C). Renvoie aussi le centre O.
 */
export const paraParVecteurs = (A, u, v) => {
    const B = { x: A.x + u.x, y: A.y + u.y };
    const D = { x: A.x + v.x, y: A.y + v.y };
    const C = { x: A.x + u.x + v.x, y: A.y + u.y + v.y };
    const O = milieu(A, C);
    return { A, B, C, D, O };
};

/** Texte libre positionné (étiquettes de mesure/longueur). */
export const txtAt = (fig, x, y, txt, { couleur = '#334155', fs = 12, anchor = 'middle' } = {}) =>
    fig.brut(`<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" font-family="sans-serif" font-size="${fs}" fill="${couleur}" text-anchor="${anchor}">${txt}</text>`);

/**
 * Dessine un quadrilatère nommé (4 sommets en ORDRE), cadré dans `vb`.
 * Options :
 *   noms       : [n0,n1,n2,n3] étiquettes des sommets (ordre des points).
 *   diagonales : true → trace les deux diagonales (fines).
 *   centre     : {label} → marque le centre O des diagonales (croix + nom).
 *   codesCotes : [[i,j,marques], …] codage d'égalité sur le côté sommet i→j.
 *   anglesEgaux: [[i, texte, classe?], …] marque l'angle intérieur au sommet i.
 *   droits     : [i, …] codage d'angle droit intérieur au sommet i.
 *   longueurs  : [[i,j,texte,decal?], …] étiquette de longueur sur [i j].
 *   pointilles : [[P,Q], …] segments pointillés bruts (hauteurs…), coords brutes.
 * Renvoie { fig, P } où P = sommets cadrés (indexés), pour composer d'autres tracés.
 */
export const dessineQuad = (sommets, vb, s, opts = {}) => {
    const {
        noms = [], diagonales = false, centre = null, codesCotes = [],
        anglesEgaux = [], droits = [], longueurs = [], pad = 46,
        pointilles = [],
    } = opts;
    const cad = cadreur(sommets, vb, pad);
    const P = sommets.map(cad);
    const fig = creerFigure(vb, s);

    // Côtés (contour fermé)
    for (let i = 0; i < P.length; i++) fig.segment(P[i], P[(i + 1) % P.length]);

    // Diagonales
    if (diagonales) {
        fig.segment(P[0], P[2], 'dg', 1.2);
        fig.segment(P[1], P[3], 'dg', 1.2);
    }

    // Centre O
    if (centre) {
        const O = milieu(P[0], P[2]);
        const col = centre.couleur || ROUGE;
        fig.brut(`<path class="cx" d="M ${(O.x - 5).toFixed(1)},${O.y.toFixed(1)} L ${(O.x + 5).toFixed(1)},${O.y.toFixed(1)} M ${O.x.toFixed(1)},${(O.y - 5).toFixed(1)} L ${O.x.toFixed(1)},${(O.y + 5).toFixed(1)}" stroke="${col}" stroke-width="1.6"/>`);
        const G = { x: (P[0].x + P[1].x + P[2].x + P[3].x) / 4, y: (P[0].y + P[1].y + P[2].y + P[3].y) / 4 };
        // étiquette du centre décalée légèrement du barycentre (≈ O)
        txtAt(fig, O.x + 9, O.y - 7, centre.label || 'O', { couleur: col, fs: 14 });
        void G;
    }

    // Codages d'égalité de côtés
    codesCotes.forEach(([i, j, marques = 1]) => {
        const a = P[i], b = P[j];
        const M = milieu(a, b);
        const d = angleVers(a, b) + 90;
        const rad = (deg) => (deg * Math.PI) / 180;
        for (let k = 0; k < marques; k++) {
            const t = (k - (marques - 1) / 2) * 5;
            // déplacement le long du côté
            const ux = (b.x - a.x) / (dist(a, b) || 1), uy = (b.y - a.y) / (dist(a, b) || 1);
            const cx = M.x + ux * t, cy = M.y + uy * t;
            const nx = Math.cos(rad(d)) * 5, ny = -Math.sin(rad(d)) * 5;
            fig.brut(`<line class="tk" x1="${(cx + nx).toFixed(1)}" y1="${(cy + ny).toFixed(1)}" x2="${(cx - nx).toFixed(1)}" y2="${(cy - ny).toFixed(1)}" stroke="#0f172a" stroke-width="1.8"/>`);
        }
    });

    // Marques d'angle intérieur
    anglesEgaux.forEach(([i, texte, classe = 'mv']) => {
        const V = P[i], Pp = P[(i + 3) % 4], Q = P[(i + 1) % 4];
        marqueInterieur(fig, V, Pp, Q, texte, { classeTexte: classe });
    });

    // Angles droits
    droits.forEach((i) => {
        const V = P[i], Pp = P[(i + 3) % 4], Q = P[(i + 1) % 4];
        codageDroitInterieur(fig, V, Pp, Q);
    });

    // ÉTIQUETTES DE LONGUEUR — dehors, contre leur côté, sans se chevaucher.
    //
    // Elles étaient posées perpendiculairement au côté, toujours du MÊME bord
    // (`angleVers(a, b) + 90`), sans regarder ni la figure ni les autres étiquettes.
    // Sur un parallélogramme aplati, le milieu de la base et celui du côté oblique
    // tombent presque au même endroit : « 9 cm » et « 10 cm » se superposaient
    // (retour prof 2026-09-27). On sort désormais du côté opposé au barycentre —
    // donc à l'extérieur, le quadrilatère étant convexe — et on écarte l'étiquette
    // juste assez pour ne toucher ni un trait ni une étiquette déjà posée.
    const Gq = { x: (P[0].x + P[1].x + P[2].x + P[3].x) / 4, y: (P[0].y + P[1].y + P[2].y + P[3].y) / 4 };
    const cotesFig = P.map((pt, k) => [pt, P[(k + 1) % P.length]]);
    const posees = [];
    const distSg = (Q, A, B) => {
        const vx = B.x - A.x, vy = B.y - A.y;
        const L2 = vx * vx + vy * vy;
        const t = L2 ? Math.max(0, Math.min(1, ((Q.x - A.x) * vx + (Q.y - A.y) * vy) / L2)) : 0;
        return Math.hypot(Q.x - (A.x + t * vx), Q.y - (A.y + t * vy));
    };
    longueurs.forEach(([i, j, texte, decal = 14]) => {
        const a = P[i], b = P[j];
        const M = milieu(a, b);
        // normale au côté, orientée vers l'extérieur
        const L = dist(a, b) || 1;
        let nx = -(b.y - a.y) / L, ny = (b.x - a.x) / L;
        if ((M.x + nx - Gq.x) ** 2 + (M.y + ny - Gq.y) ** 2 < (M.x - Gq.x) ** 2 + (M.y - Gq.y) ** 2) { nx = -nx; ny = -ny; }
        const demi = (String(texte).length * 12 * 0.55) / 2;
        let choix = null;
        [decal, decal + 6, decal + 12, decal + 19].forEach((d) => {
            const C = { x: M.x + nx * d, y: M.y + ny * d };
            const sondes = [C, { x: C.x - demi, y: C.y }, { x: C.x + demi, y: C.y }];
            const libre = Math.min(
                ...sondes.flatMap((Q) => cotesFig.map(([A, B]) => distSg(Q, A, B))),
                ...sondes.flatMap((Q) => (posees.length ? posees.map(([A, B]) => distSg(Q, A, B)) : [99])),
            );
            if (!choix && libre >= 7) choix = C;
        });
        const T = choix || { x: M.x + nx * (decal + 19), y: M.y + ny * (decal + 19) };
        posees.push([{ x: T.x - demi, y: T.y }, { x: T.x + demi, y: T.y }]);
        txtAt(fig, T.x, T.y + 4, texte, { couleur: '#334155' });
    });

    // Segments pointillés bruts (coords à cadrer)
    pointilles.forEach(([Praw, Qraw]) => {
        segmentPointille(fig, cad(Praw), cad(Qraw));
    });

    // Sommets nommés (étiquette poussée vers l'extérieur depuis le barycentre)
    const G = { x: (P[0].x + P[1].x + P[2].x + P[3].x) / 4, y: (P[0].y + P[1].y + P[2].y + P[3].y) / 4 };
    P.forEach((pt, i) => {
        if (!noms[i]) return;
        const dir = angleVers(G, pt);
        fig.point(pt, noms[i], dir, 14);
    });

    return { fig, P };
};

/**
 * Élément additionnel d'énoncé prêt à l'emploi depuis une liste de sommets.
 */
export const elParallelo = (sommets, vb, s, opts) => figEl(dessineQuad(sommets, vb, s, opts).fig.render());
